import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Eye, Clock, Heart, Coins, UsersThree, ChatCircle,
  ChartBar, SpeakerHigh, CaretRight,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { track } from "../lib/firebase";
import MilestoneBadges from "../components/MilestoneBadges";

function fmtWatch(seconds) {
  const s = Math.floor(seconds || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function fmtNum(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n || 0);
}

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    track("analytics_dashboard_view");
    api.get("/creator/analytics").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) {
    return (
      <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">
        Crunching numbers…
      </div>
    );
  }

  const maxDaily = Math.max(1, ...(data.daily_earnings || []).map((d) => d.pearls));

  const stats = [
    { icon: Eye, label: "Views", value: fmtNum(data.total_views), accent: "text-sky-300" },
    { icon: Clock, label: "Watch time", value: fmtWatch(data.total_watch_seconds), accent: "text-papaya" },
    { icon: Heart, label: "Likes", value: fmtNum(data.total_likes), accent: "text-red-400" },
    { icon: Coins, label: "Gift pearls", value: fmtNum(data.gift_pearls), accent: "text-jam-gold" },
    { icon: UsersThree, label: "Followers", value: fmtNum(data.followers), accent: "text-teal-300" },
    { icon: ChatCircle, label: "Comments", value: fmtNum(data.total_comments), accent: "text-cream/70" },
  ];

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8" data-testid="analytics-page">
      <Link to="/settings" className="inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-[0.2em] uppercase font-bold mb-4">
        <ArrowLeft size={14} /> Settings
      </Link>
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator Analytics</p>
      <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">Your numbers</h1>
      <p className="text-cream/50 text-sm mt-1">{data.posts_count || 0} posts · lifetime {fmtNum(data.total_pearls_earned)} pearls earned</p>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6" data-testid="analytics-stats">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl crystal-glass p-4" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
            <s.icon size={18} className={s.accent} weight="duotone" />
            <p className="text-cream text-2xl font-black tracking-tighter mt-2">{s.value}</p>
            <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <MilestoneBadges />

      {/* 7-day gift earnings */}
      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-3 flex items-center gap-2">
          <ChartBar size={14} className="text-jam-gold" weight="fill" /> Gift earnings · last 7 days
        </p>
        {(data.daily_earnings || []).length === 0 ? (
          <p className="text-cream/40 text-sm rounded-2xl bg-white/5 border border-white/5 p-4">
            No gift earnings this week yet — go live or start a gift battle to spark gifting!
          </p>
        ) : (
          <div className="rounded-2xl crystal-glass p-4 flex items-end gap-2 h-36" data-testid="earnings-chart">
            {data.daily_earnings.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-jam-gold text-[9px] font-black mb-1">{d.pearls}</span>
                <div
                  className="w-full rounded-t-lg bg-gradient-to-t from-jam-gold/40 to-jam-gold"
                  style={{ height: `${Math.max(6, (d.pearls / maxDaily) * 100)}%` }}
                />
                <span className="text-cream/40 text-[8px] mt-1">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Top posts */}
      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-3">Top posts</p>
        {(data.top_posts || []).length === 0 ? (
          <p className="text-cream/40 text-sm">No posts yet — share your first dance!</p>
        ) : (
          <div className="space-y-2" data-testid="top-posts">
            {data.top_posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/p/${p.id}`}
                className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/40 transition-colors"
                data-testid={`top-post-${i + 1}`}
              >
                <span className="text-papaya font-black text-lg w-6 text-center">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-cream text-sm font-bold truncate">{p.caption || "Untitled"}</p>
                  <p className="text-cream/50 text-[11px] flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Eye size={11} /> {fmtNum(p.views_count || 0)}</span>
                    <span className="flex items-center gap-1"><Heart size={11} /> {fmtNum(p.likes_count || 0)}</span>
                    <span className="flex items-center gap-1"><Clock size={11} /> {fmtWatch(p.watch_seconds)}</span>
                    {p.voice_drop_path && <span className="flex items-center gap-1 text-sky-300"><SpeakerHigh size={11} weight="fill" /> drop</span>}
                  </p>
                </div>
                <CaretRight size={14} className="text-cream/30" />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
